import { useEffect, useRef } from "react";

// Lightweight scroll-reveal hook. Adds the `is-visible` class to any
// descendant carrying the `jingle-reveal` class once it scrolls into
// view. The CSS handles the transition itself; this hook only flips
// the bit.
//
// Important: the marketing page lazy-loads several sections via
// React.Suspense. Those sections' `.jingle-reveal` nodes are NOT in
// the DOM when this hook first runs, so we also watch for new
// reveal targets via MutationObserver. Without this, late-mounting
// sections would stay stuck at `opacity: 0` forever (the symptom
// users see as a giant empty starfield below the audience cards).
export function useReveal() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const noObserver = typeof IntersectionObserver === "undefined";

    if (noObserver || reduceMotion) {
      const reveal = () => {
        root
          .querySelectorAll<HTMLElement>(".jingle-reveal")
          .forEach((el) => el.classList.add("is-visible"));
      };
      reveal();
      // Still watch for late-mounting sections so their content
      // doesn't get stuck invisible.
      const mo = new MutationObserver(reveal);
      mo.observe(root, { childList: true, subtree: true });
      return () => mo.disconnect();
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 },
    );

    const observed = new WeakSet<Element>();
    function observeReveals(scope: ParentNode) {
      scope
        .querySelectorAll<HTMLElement>(".jingle-reveal")
        .forEach((el) => {
          if (observed.has(el)) return;
          observed.add(el);
          // If element already in the viewport (e.g. above the fold
          // when a lazy section finally hydrates near the top), the
          // observer will fire on the next tick and reveal it.
          io.observe(el);
        });
    }
    observeReveals(root);

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          const el = node as Element;
          if (el.matches?.(".jingle-reveal")) {
            if (!observed.has(el)) {
              observed.add(el);
              io.observe(el);
            }
          }
          observeReveals(el);
        });
      }
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);

  return rootRef;
}
